import React from 'react';
import { Grid, Typography } from '@material-ui/core';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { createOfferCurrency as currency } from 'common/oldJavascripts/utils/format';
import isNonUnionWorkScheduleA from 'common/utilities/isNonUnionWorkScheduleA';
import useFeatureFlags from 'common/hooks/useFeatureFlags';

const formatAmount = amount => {
  return !Number.isInteger(amount) ? currency(amount) : `$${amount}`;
};
const employeeInfoStyles = makeStyles(theme => ({
  title: {
    color: '#999999',
    fontSize: 12,
    fontWeight: 400,
  },
  name: {
    fontSize: 24,
    fontWeight: 400,
    color: '#646464',
  },
  date: {
    fontSize: 14,
    color: '#646464',
    fontWeight: 400,
  },
  email: {
    fontSize: 10,
    color: '#0091ff',
    fontWeight: 400,
    marginTop: '3px',
    letterSpacing: '0.3px',
  },
  spacing: {
    marginTop: '20px',
    marginBottom: '10px',
  },
  amount: {
    color: '#2eaba4',
    fontSize: 24,
    fontWeight: 400,
    marginRight: '10px',
  },
  description: {
    wordBreak: 'break-word',
  },
}));

const EmployeeInfo = ({ offer }) => {
  const classes = employeeInfoStyles();
  const { hourlyRate, weeklyRate, dailyRate } = getRate(offer);
  const workScheduleCode = offer?.work_schedule_code;
  const flags = useFeatureFlags();
  const isNonUnion = offer?.union_is_non_union;
  const { terms_of_employment } = offer;
  const {
    overtime_rate: overtimeRate,
    pay_6th_day_rate: sixthDayRate,
    pay_7th_day_rate: seventhDayRate,
    guaranteed_hours_studio: hoursPerDay,
  } = terms_of_employment;

  const isNonUnionScheduleAFlagActive = flags.includes('nonUnionSchedules');
  const isNonUnionScheduleCFlagActive = flags.includes('nonUnionScheduleC');
  const isNonUnionScheduleA =
    isNonUnion &&
    isNonUnionScheduleAFlagActive &&
    isNonUnionWorkScheduleA(workScheduleCode);
  const isNonUnionScheduleC =
    isNonUnion &&
    isNonUnionScheduleCFlagActive &&
    ['C', 'E', 'CN'].includes(workScheduleCode);

  const payDayTitle = day => {
    if (isNonUnionScheduleA) {
      return (
        <>
          Apply Hours per <br /> Day to {day}th Day
        </>
      );
    }
    if (isNonUnionScheduleC) {
      return `Pay at ${day}th day`;
    }
    return `${day}th day pay`;
  };

  const weekendPayRate = payRate => {
    if (isNonUnionScheduleA) return payRate > 0 ? 'Yes' : 'No';
    if (isNonUnionScheduleC) {
      const weekendRate = (
        parseFloat(payRate || 0) / parseFloat(weeklyRate || 0)
      ).toFixed(1);
      return `${weekendRate}x`;
    }
    return formatAmount(payRate);
  };

  return (
    <div>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.title}>
            Employee
          </Typography>
          <Typography variant="h2" className={classes.name}>
            {`${offer.first_name}  ${offer.last_name}`}
          </Typography>
          <Typography variant="h6" className={classes.email}>
            {offer.email}
          </Typography>
        </Grid>
      </Grid>
      <Grid container className={classes.spacing}>
        <Grid item xs={8}>
          <Typography variant="h6" className={classes.title}>
            Position/Title (Occupation Code)
          </Typography>
          <Typography className={classes.description}>
            {offer.occupation_description}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="h6" className={classes.title}>
            Start Date
          </Typography>
          <Typography className={classes.date}>
            {moment(offer.start_date).format('MM/DD/YYYY')}
          </Typography>
        </Grid>
      </Grid>
      <Grid container className={classes.spacing}>
        <Grid item xs={8}>
          <Typography variant="h6" className={classes.title}>
            Union
          </Typography>
          <Typography className={classes.description}>
            {offer.union_description}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="h6" className={classes.title}>
            Department
          </Typography>
          <Typography className={classes.description}>
            {offer.department_name}
          </Typography>
        </Grid>
      </Grid>
      <Grid container className={classes.spacing}>
        {weeklyRate && (
          <Grid item xs={4}>
            <Typography variant="h6" className={classes.title}>
              Weekly Rate
            </Typography>
            <Typography className={classes.amount}>
              {currency(weeklyRate)}
            </Typography>
          </Grid>
        )}
        {hourlyRate && (
          <Grid item xs={4}>
            <Typography variant="h6" className={classes.title}>
              Hourly
            </Typography>
            <Typography className={classes.amount}>
              {currency(hourlyRate)}
            </Typography>
          </Grid>
        )}
        {dailyRate && (
          <Grid item xs={4}>
            <Typography variant="h6" className={classes.title}>
              Daily
            </Typography>
            <Typography className={classes.amount}>
              {currency(dailyRate)}
            </Typography>
          </Grid>
        )}
        {isNonUnionScheduleA && (
          <>
            {overtimeRate && (
              <Grid item xs={4}>
                <Typography variant="h6" className={classes.title}>
                  Overtime Rate
                </Typography>
                <Typography className={classes.amount}>
                  {currency(overtimeRate)}
                </Typography>
              </Grid>
            )}
            {hoursPerDay && (
              <Grid item xs={4}>
                <Typography variant="h6" className={classes.title}>
                  Hours per Day
                </Typography>
                <Typography className={classes.amount}>
                  {hoursPerDay}
                </Typography>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Grid container className={classes.spacing}>
        {sixthDayRate && (
          <Grid item xs={4}>
            <Typography variant="h6" className={classes.title}>
              {payDayTitle('6')}
            </Typography>
            <Typography className={classes.description}>
              {weekendPayRate(sixthDayRate)}
            </Typography>
          </Grid>
        )}
        {seventhDayRate && (
          <Grid item xs={4}>
            <Typography variant="h6" className={classes.title}>
              {payDayTitle('7')}
            </Typography>
            <Typography className={classes.description}>
              {weekendPayRate(seventhDayRate)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

const getRate = offer => {
  const { terms_of_employment = {} } = offer;
  const {
    rate_per_week_studio,
    rate_per_week_location,
    rate_per_day_studio,
    rate_per_hour_location,
    rate_per_hour_studio,
  } = terms_of_employment;

  return {
    hourlyRate: rate_per_hour_studio || rate_per_hour_location,
    dailyRate: rate_per_day_studio || rate_per_day_studio,
    weeklyRate: rate_per_week_studio || rate_per_week_location,
  };
};

export default EmployeeInfo;
